import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    ConnectorItemParametricState,
    EntityItemParametricState,
    ItemParametricState,
} from '@toolproof-core/visualization/_contract/item';
import type { ActivationLink } from '@toolproof-core/visualization/_contract/item-activation';
import type { ConstellationKey, ItemKey } from '@toolproof-core/visualization/_contract/key';

import {
    WORKFLOW_KEYS,
    buildCaseConstellationKey,
    buildRoleConstellationKey,
    buildToolItemKey,
    buildToolStepItemKey,
    buildWorkflowStateEntryConstellationKey,
    parseRoleCollectionKey,
    parseRoleItemKey,
    parseWorkflowStateEntryCollectionKey,
    parseWorkflowStateEntryItemKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import type {
    ItemParametricStateByKey,
    ItemParametricStateEntry,
} from '@/workflow/_pure/derivation/itemParametricState';
import type { CaseMetadata } from '@/workflow/_contract/kernel';

export { buildToolStepItemKey } from '@/workflow/_pure/keyCodec';

function clamp01(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(1, value));
}

export function buildItemParametricState(opacity: number): ItemParametricStateEntry {
    return {
        visualStyle: {
            opacity: clamp01(opacity),
        },
        visibility: {
            visible: opacity > 0,
        },
    };
}

export function setItemParametricState(
    itemParametricStateByKey: ItemParametricStateByKey,
    itemKey: ItemKey,
    opacity: number,
): void {
    itemParametricStateByKey[itemKey] = buildItemParametricState(opacity);
}

export function appendActivationLink(args: {
    links: ActivationLink[];
    sourceConstellationKey: ActivationLink['sourceConstellationKey'];
    targetConstellationKey: ActivationLink['targetConstellationKey'];
    sourceItemKey: ItemKey<'entity'>;
    targetItemKey: ItemKey<'entity'>;
}) {
    const {
        links,
        sourceConstellationKey,
        targetConstellationKey,
        sourceItemKey,
        targetItemKey,
    } = args;

    let pairLink = links.find((candidate) => (
        candidate.sourceConstellationKey === sourceConstellationKey
        && candidate.targetConstellationKey === targetConstellationKey
    ));

    if (!pairLink) {
        pairLink = {
            sourceConstellationKey,
            targetConstellationKey,
            targetItemKeysBySourceItemKey: {},
        };
        links.push(pairLink);
    }

    const targetItemKeys = pairLink.targetItemKeysBySourceItemKey[sourceItemKey] ?? [];
    if (!targetItemKeys.includes(targetItemKey)) {
        targetItemKeys.push(targetItemKey);
    }
    pairLink.targetItemKeysBySourceItemKey[sourceItemKey] = targetItemKeys;
}

export function deriveActivationLinks(args: {
    itemCollections: ItemCollection[];
    toolStepConstellationKey: ConstellationKey<'entity'>;
    traceSnapshot: {
        position: {
            orderedToolSteps: Array<{
                stepKey: string;
                toolHandle: ToolHandle;
            }>;
        };
    } | undefined;
    activeToolStep: {
        toolHandle: ToolHandle;
        stepKey: string;
    } | undefined;
    caseMetadataByItemKey?: ReadonlyMap<ItemKey<'entity'>, CaseMetadata>;
    workflowKeyScope?: WorkflowKeyScope;
}): ActivationLink[] {
    const {
        itemCollections,
        traceSnapshot,
        activeToolStep,
        caseMetadataByItemKey,
    } = args;
    if (!traceSnapshot) return [];

    const links: ActivationLink[] = [];
    const orderedToolSteps = traceSnapshot.position.orderedToolSteps;
    const toolStepItemKeysByToolHandle = new Map<ToolHandle, Set<ItemKey<'entity'>>>();

    for (const toolStep of orderedToolSteps) {
        const toolStepItemKey = buildToolStepItemKey(toolStep.stepKey);
        let toolStepKeySet = toolStepItemKeysByToolHandle.get(toolStep.toolHandle);
        if (!toolStepKeySet) {
            toolStepKeySet = new Set<ItemKey<'entity'>>();
            toolStepItemKeysByToolHandle.set(toolStep.toolHandle, toolStepKeySet);
        }
        toolStepKeySet.add(toolStepItemKey);

        appendActivationLink({
            links,
            sourceConstellationKey: args.toolStepConstellationKey,
            targetConstellationKey: WORKFLOW_KEYS.constellations.tool,
            sourceItemKey: toolStepItemKey,
            targetItemKey: buildToolItemKey(toolStep.toolHandle),
        });
    }

    if (activeToolStep) {
        let toolStepKeySet = toolStepItemKeysByToolHandle.get(activeToolStep.toolHandle);
        if (!toolStepKeySet) {
            toolStepKeySet = new Set<ItemKey<'entity'>>();
            toolStepItemKeysByToolHandle.set(activeToolStep.toolHandle, toolStepKeySet);
        }
        toolStepKeySet.add(buildToolStepItemKey(activeToolStep.stepKey));
    }

    for (const [toolHandle, toolStepItemKeySet] of toolStepItemKeysByToolHandle.entries()) {
        const sourceToolItemKey = buildToolItemKey(toolHandle);
        for (const toolStepItemKey of toolStepItemKeySet) {
            appendActivationLink({
                links,
                sourceConstellationKey: WORKFLOW_KEYS.constellations.tool,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: sourceToolItemKey,
                targetItemKey: toolStepItemKey,
            });
        }
    }

    if (caseMetadataByItemKey && caseMetadataByItemKey.size > 0 && args.workflowKeyScope) {
        for (const [caseItemKey, metadata] of caseMetadataByItemKey.entries()) {
            const caseConstellationKey = buildCaseConstellationKey({
                ...args.workflowKeyScope,
                ownerMacroStepKey: metadata.ownerMacroStepKey,
            });
            const whenToolStepItemKey = buildToolStepItemKey(metadata.whenToolStepKey);
            const whatToolStepItemKey = buildToolStepItemKey(metadata.whatToolStepKey);

            appendActivationLink({
                links,
                sourceConstellationKey: caseConstellationKey,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: caseItemKey,
                targetItemKey: whenToolStepItemKey,
            });
            appendActivationLink({
                links,
                sourceConstellationKey: caseConstellationKey,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: caseItemKey,
                targetItemKey: whatToolStepItemKey,
            });

            appendActivationLink({
                links,
                sourceConstellationKey: args.toolStepConstellationKey,
                targetConstellationKey: caseConstellationKey,
                sourceItemKey: whenToolStepItemKey,
                targetItemKey: caseItemKey,
            });
            appendActivationLink({
                links,
                sourceConstellationKey: args.toolStepConstellationKey,
                targetConstellationKey: caseConstellationKey,
                sourceItemKey: whatToolStepItemKey,
                targetItemKey: caseItemKey,
            });
        }
    }

    if (!activeToolStep) return links;

    type RoleIdentity = `${'input' | 'output'}:${string}:${string}`;
    const roleByIdentity = new Map<RoleIdentity, {
        constellationKey: ReturnType<typeof buildRoleConstellationKey>;
        itemKey: ItemKey<'entity'>;
    }>();

    const workflowStateEntryByIdentity = new Map<RoleIdentity, {
        constellationKey: ReturnType<typeof buildWorkflowStateEntryConstellationKey>;
        itemKey: ItemKey<'entity'>;
    }>();

    for (const itemCollection of itemCollections) {
        if (itemCollection.itemCollectionKind !== 'entity') continue;

        const roleCollection = parseRoleCollectionKey(itemCollection.itemCollectionKey);
        if (roleCollection) {
            if (roleCollection.toolHandle !== activeToolStep.toolHandle) continue;

            const roleConstellationKey = buildRoleConstellationKey({
                toolHandle: roleCollection.toolHandle,
                direction: roleCollection.direction,
                resourceTypeHandle: roleCollection.resourceTypeHandle,
            });

            for (const roleItem of itemCollection.items as unknown as Array<{
                itemKey: ItemKey<'entity'>;
                kind: unknown;
                roleDirection: string;
                role: string;
                roleGroup: string;
            }>) {
                const parsedRoleItem = parseRoleItemKey(roleItem.itemKey);
                if (!parsedRoleItem) continue;

                const roleIdentity = `${parsedRoleItem.direction}:${parsedRoleItem.resourceTypeHandle}:${parsedRoleItem.roleName}` as RoleIdentity;
                roleByIdentity.set(roleIdentity, {
                    constellationKey: roleConstellationKey,
                    itemKey: roleItem.itemKey,
                });
            }

            continue;
        }

        const workflowStateEntryCollection = parseWorkflowStateEntryCollectionKey(itemCollection.itemCollectionKey);
        if (!workflowStateEntryCollection) continue;

        const workflowStateEntryConstellationKey = buildWorkflowStateEntryConstellationKey({
            workflowHandle: workflowStateEntryCollection.workflowHandle,
            threadIndex: workflowStateEntryCollection.threadIndex,
            stepKey: workflowStateEntryCollection.stepKey,
            resourceTypeHandle: workflowStateEntryCollection.resourceTypeHandle,
            direction: workflowStateEntryCollection.direction,
        });

        for (const workflowStateEntryItem of itemCollection.items as unknown as Array<{
            itemKey: ItemKey<'entity'>;
            kind: unknown;
        }>) {
            const parsedWorkflowStateEntryItem = parseWorkflowStateEntryItemKey(workflowStateEntryItem.itemKey);
            if (!parsedWorkflowStateEntryItem || parsedWorkflowStateEntryItem.stepKey !== activeToolStep.stepKey) continue;

            const roleIdentity = `${parsedWorkflowStateEntryItem.direction}:${parsedWorkflowStateEntryItem.resourceTypeHandle}:${parsedWorkflowStateEntryItem.roleName}` as RoleIdentity;
            workflowStateEntryByIdentity.set(roleIdentity, {
                constellationKey: workflowStateEntryConstellationKey,
                itemKey: workflowStateEntryItem.itemKey,
            });
        }
    }

    for (const [roleIdentity, roleEntry] of roleByIdentity.entries()) {
        const workflowStateEntryRef = workflowStateEntryByIdentity.get(roleIdentity);
        if (!workflowStateEntryRef) continue;

        appendActivationLink({
            links,
            sourceConstellationKey: roleEntry.constellationKey,
            targetConstellationKey: workflowStateEntryRef.constellationKey,
            sourceItemKey: roleEntry.itemKey,
            targetItemKey: workflowStateEntryRef.itemKey,
        });
    }

    return links;
}
